import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Network, Wifi, Globe, Zap, Cloud, Shield, Clock, ArrowRight } from 'lucide-react';

const NetworkServices = () => {
  const services = [
    {
      icon: Zap,
      title: 'Dedicated Internet Access',
      description: 'Low-latency, high-performance internet connectivity with guaranteed bandwidth and 99.95% uptime SLA'
    },
    {
      icon: Network,
      title: 'SD-WAN Connectivity',
      description: 'Dynamic WAN path selection, centralized orchestration, and optimized application performance'
    },
    {
      icon: Wifi,
      title: 'Managed Wi-Fi',
      description: 'Enterprise-grade wireless solutions with seamless roaming and centralized management'
    },
    {
      icon: Cloud,
      title: 'Multi-Cloud Connectivity',
      description: 'High-security private links to AWS, Azure, Google Cloud, and other cloud platforms'
    },
    {
      icon: Globe,
      title: 'Internet Leased Lines (ILL)',
      description: 'Point-to-point dedicated connectivity for enterprise and data center interconnection'
    },
    {
      icon: Shield,
      title: 'DDoS Protection',
      description: 'Advanced DDoS mitigation to protect your services from online threats'
    },
    {
      icon: Network,
      title: 'MPLS & VPN Solutions',
      description: 'Secure private network connectivity for multi-site enterprises'
    },
    {
      icon: Clock,
      title: 'Event Connectivity',
      description: 'Temporary high-bandwidth connectivity for exhibitions, events, and conferences'
    }
  ];

  const offerings = [
    {
      category: 'For Businesses',
      items: [
        'Dedicated Internet Access with guaranteed bandwidth',
        'Smart ILL for branch connectivity',
        'Managed Wi-Fi solutions',
        'SD-WAN for optimized application delivery',
        'VPN services for secure remote access',
        'Event connectivity for temporary requirements'
      ]
    },
    {
      category: 'For Enterprises',
      items: [
        'Data center interconnection',
        'Cloud-based networking solutions',
        'Edge computing services',
        'Multi-cloud connectivity',
        'Disaster recovery links',
        'High-availability network design'
      ]
    },
    {
      category: 'For Residential Users',
      items: [
        'High-speed fiber broadband plans',
        'IPTV and OTT services',
        'Bundled voice and data services',
        'Smart home connectivity',
        'Gaming-optimized connections',
        '24/7 customer support'
      ]
    }
  ];

  const features = [
    {
      title: '99.95% Uptime SLA',
      description: 'Industry-leading service availability with redundant infrastructure'
    },
    {
      title: '24/7 NOC Support',
      description: 'Round-the-clock network monitoring and rapid issue resolution'
    },
    {
      title: 'IPv6 Ready',
      description: 'Future-proof infrastructure supporting next-generation protocols'
    },
    {
      title: 'Scalable Bandwidth',
      description: 'Easy bandwidth upgrades as your business grows'
    },
    {
      title: 'Class A ISP',
      description: 'Licensed Internet Service Provider with pan-India presence'
    },
    {
      title: 'Low Latency',
      description: 'Optimized routing for ultra-low latency connectivity'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-950 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=1920&h=800&fit=crop" 
            alt="Network Services" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Network className="h-16 md:h-20 w-16 md:w-20 mx-auto mb-6 text-blue-300 animate-float" />
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">Network & ISP Services</h1>
            <p className="text-lg md:text-xl text-blue-100 mb-8">
              High-performance connectivity solutions with ultra-low latency and 24/7 support
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-900 hover:bg-blue-50 text-base md:text-lg px-6 md:px-8 transition-all hover:scale-105">
                Get Connected
              </Button>
            </Link>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Enterprise-Grade Connectivity</h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600">
              As a Class A ISP, we provide last-mile delivery for major telcos and enterprise-grade connectivity solutions. Our network spans 200+ PoPs across India, ensuring reliable service delivery wherever your business operates.
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  <CardHeader>
                    <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="h-7 w-7 text-blue-600" />
                    </div>
                    <CardTitle className="text-lg">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 text-sm">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service Offerings */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Tailored Solutions for Every Need</h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto mb-6"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {offerings.map((offering, index) => (
              <Card key={index} className="border-none shadow-lg">
                <CardHeader>
                  <CardTitle className="text-2xl text-blue-900 text-center">{offering.category}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {offering.items.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                        </div>
                        <span className="text-gray-600 text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Our Network Services?</h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto mb-6"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {features.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-8 h-8 bg-blue-600 rounded-full"></div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center">Industries We Serve</h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto mb-12"></div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Events & Exhibitions</h3>
                <p className="text-gray-600">High-bandwidth temporary connectivity for trade shows, conferences, and exhibitions</p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Educational Institutions</h3>
                <p className="text-gray-600">Reliable connectivity for campuses, e-learning platforms, and digital classrooms</p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Airports & Regulatory Zones</h3>
                <p className="text-gray-600">Secure, high-availability networks for critical infrastructure</p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Smart Cities & Housing</h3>
                <p className="text-gray-600">FTTH, community Wi-Fi, and smart infrastructure connectivity</p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Banking & Finance</h3>
                <p className="text-gray-600">Secure, low-latency connectivity for financial transactions</p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Healthcare</h3>
                <p className="text-gray-600">Reliable networks for telemedicine, patient records, and hospital operations</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-blue-950 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">Experience Next-Level Connectivity</h2>
          <p className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto">
            Contact us to discuss your network requirements and get a customized solution.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-blue-50 text-lg px-8">
              Request a Quote
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default NetworkServices;